import i18next, { type TFunction } from 'i18next';

import type { Category } from '../api/types.ts';
import { ur } from '../i18n/ur.ts';

/** The categories seeded by backend migrations 0002 and 0046, by icon key. */
const SEEDED: Record<string, string> = {
  veg: 'Raw material / vegetables',
  fruit: 'Fruits', // migration 0046
  meat: 'Chicken & meat',
  rice: 'Rice & flour',
  dairy: 'Dairy & eggs',
  oil: 'Spices & oil',
  water: 'Drinking water 20 L',
  kitchen: 'Kitchen accessories',
  clean: 'Tissues & cleaning',
  washroom: 'Washroom accessories',
  stationery: 'Stationery',
  printing: 'Printing',
  petrol: 'Petrol',
  generator: 'Generator fuel',
  electricity: 'Electricity',
  gas: 'Gas',
  phone: 'Phone',
  internet: 'Internet',
  repair: 'Repairs',
  plumbing: 'Plumbing & electrical',
  furniture: 'Furniture',
  misc: 'Miscellaneous',
};

/** True for one of the built-in categories (unrenamed), which have app-translated labels. */
export const isSeeded = (c: Pick<Category, 'name' | 'icon'>) => SEEDED[c.icon] === c.name;

/** The Urdu label the app shows for a built-in category (null for categories owners added). */
export function builtInUrdu(c: Pick<Category, 'name' | 'icon'>): string | null {
  return isSeeded(c) ? ((ur.cat as Record<string, string>)[c.icon] ?? null) : null;
}

/**
 * Tile label for a category.
 * - In Urdu, an Urdu name the owner saved always wins (rule 8).
 * - Otherwise the built-in categories use the app's short translated labels ("Spices & oil" →
 *   "Oil & spice" / "تیل مصالحہ").
 * - Categories owners add show their own name.
 */
export function categoryLabel(c: Category, t: TFunction): string {
  if (i18next.language === 'ur' && c.nameUr) return c.nameUr;
  if (isSeeded(c)) {
    const key = `cat.${c.icon}`;
    const label = t(key);
    if (label !== key) return label;
  }
  return c.name;
}

/** A light tint of an entity colour for icon backgrounds (14% over white). */
export function tintOf(hex: string): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const mix = (c: number) => Math.round(255 - (255 - c) * 0.14);
  const r = mix((n >> 16) & 255);
  const g = mix((n >> 8) & 255);
  const b = mix(n & 255);
  return `rgb(${r} ${g} ${b})`;
}
